import React, { useContext, useEffect, useState } from 'react'
import '@resources/fontes/fontes.css'
import '@resources/css/donators.css'
import AuthContext from '@contexts/Auth'
import { PhoneInput } from 'react-international-phone'
import Select from 'react-select'
import makeAnimated from 'react-select/animated'
import Mask from '@helpers/Mask'
import { cpfIsValid } from '@helpers/Utils'
import { isFullName } from '@helpers/Validation'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import moment from 'moment'
import ClinicCascadeSelect, { IClinicCascadeValue } from '@components/Request/ClinicCascadeSelect'
import * as $Clinic from '@services/Clinic'

const animatedComponents = makeAnimated()

const maritalStatusOptions = [
  { value: 'Solteira', label: 'Solteira' },
  { value: 'União Estável', label: 'União Estável' },
  { value: 'Casada', label: 'Casada' },
  { value: 'Separada', label: 'Separada' },
  { value: 'Divorciada', label: 'Divorciada' },
  { value: 'Viúva', label: 'Viúva' },
]

type Props = {
  countries: any
  countriesWithBrNewMask: any
}

const ProfilePatient: React.FC<Props> = ({ countries, countriesWithBrNewMask }) => {
  const { user } = useContext(AuthContext)

  const [ data, setData ] = useState<any>({
    name: '',
    birthdate: '',
    nationality: '',
    document_type: '',
    document: '',
    whatsapp: '',
    email: '',
    marital_status: '',
  })

  const [ hasClinic, setHasClinic ] = useState(false)
  const [ clinicData, setClinicData ] = useState<IClinicCascadeValue>({
    clinic_state: '',
    clinic_city: '',
    clinic_id: '',
  })

  const SweetAlert = withReactContent(Swal)

  useEffect(() => {
    if (user) {
      setData({
        name: user.name,
        birthdate: user.birthdate,
        nationality: user.patient.nationality,
        document_type: user.document_type,
        document: user.document,
        whatsapp: user.whatsapp,
        email: user.email,
        marital_status: user.patient.marital_status,
      })

      setHasClinic(!!user.patient.has_clinic)

      if (user.patient.clinic_id) {
        $Clinic.show(user.patient.clinic_id).then(({ data: clinic }) => {
          setClinicData({
            clinic_state: clinic.main_address?.state || '',
            clinic_city: clinic.main_address?.city || '',
            clinic_id: clinic.id,
          })
        }).catch((error) => {
          console.error('Error fetching clinic:', error)
        })
      } else {
        setClinicData({ clinic_state: '', clinic_city: '', clinic_id: '' })
      }
    }
  }, [user])

  const handleHasClinic = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasClinic(e.target.checked)

    if (!e.target.checked) {
      setClinicData({ clinic_state: '', clinic_city: '', clinic_id: '' })
    }
  }

  if (!user) {
    return null
  }

  const handleUserDocumentType = (e: any) => {
    if (!user) return

    let document = data.document

    if (document) {
      document = document.replace(/\D/g, '')
      if (e.value == 'cpf') {
        document = Mask.cpf(document.substring(0, 11))
      }
    }

    setData({
      ...data,
      document_type: e.value,
      document
    })
  }

  const handleDocumentChange = (e: any) => {
    if (!user) return

    let value = e.target.value

    if (data.document_type === 'cpf') {
      value = Mask.cpf(e.target.value)

      if (value.length > 14) {
        value = value.slice(0, 14)
      }

      if (value.length === 14 && !cpfIsValid(value)) {
        value = value.slice(0, 13)

        SweetAlert.fire({
          title: 'Erro!',
          text: 'CPF inválido',
          icon: 'error',
          confirmButtonText: 'Ok',
        })
      }
    }

    setData({ ...data, document: value })
  }

  const handleNameChange = (e: any) => {
    setData({ ...data, name: e.target.value })
  }

  const handleNameBlur = () => {
    if (data.name && !isFullName(data.name)) {
      SweetAlert.fire({
        title: 'Erro!',
        text: 'Nome completo é obrigatório (nome e sobrenome).',
        icon: 'error',
        confirmButtonText: 'Ok',
      })
    }
  }

  return (
    <div className="row">
      <div className="col-12 col-sm-5 col-md-5">
        <div className="form-group">
          <label>Nome Completo (Igual no CPF/Passaporte) <span>*</span></label>
          <input
            type="text"
            placeholder=""
            className={`form-control ${!data.name || !isFullName(data.name) ? 'is-invalid' : ''}`}
            name="name"
            value={data.name}
            style={{textTransform: 'uppercase'}}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            required
          />
        </div>
      </div>

      <div className="col-12 col-sm-3 col-md-3">
        <div className="form-group">
          <label>Nascimento <span>*</span></label>
          <input
            type="date"
            className={`form-control ${!data.birthdate ? 'is-invalid' : ''}`}
            name="birthdate"
            value={data.birthdate ? moment(data.birthdate).format('YYYY-MM-DD') : ''}
            onChange={(e) => setData({ ...data, birthdate: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="col-12 col-sm-4 col-md-4">
        <div className="form-group">
          <label>Estado Civil</label>
          <Select
            className="react-select"
            placeholder={<div>Selecione</div>}
            name="marital_status"
            components={animatedComponents}
            options={maritalStatusOptions}
            isClearable={true}
            isSearchable={true}
            isDisabled={false}
            isLoading={false}
            isRtl={false}
            closeMenuOnSelect={true}
            value={data.marital_status ? { value: data.marital_status, label: data.marital_status } : null}
            onChange={(e: any) => setData({ ...data, marital_status: e?.value ?? null })}
          />
        </div>
      </div>

      <div className="form-group col-12 col-sm-4 col-md-8">
        <label>CPF/Passaporte <span>*</span>:</label>
        <div className="row m-0">
          <div className="col-md-6 p-0">
            <Select
              className={`react-select ${!data.document ? 'is-invalid' : ''}`}
              placeholder=""
              id="document_type"
              name="document_type"
              isSearchable={false}
              isClearable={false}
              options={[{ value: 'cpf', label: 'CPF' }, { value: 'passport', label: 'Passaporte' }]}
              value={data.document_type === 'passport' ? { value: 'passport', label: 'Passaporte' } : { value: 'cpf', label: 'CPF' }}
              onChange={(e) => { handleUserDocumentType(e) }}
              required
            />
          </div>
          <div className="col-md-6 p-0 mt-3 mt-md-0 pl-md-3">
            <input
              id="document"
              name="document"
              className={`form-control ${!data.document ? 'is-invalid' : ''}`}
              value={data.document ?? ''}
              onChange={(e) => handleDocumentChange(e)}
              required
            />
          </div>
        </div>
      </div>

      <div className="col-12 col-sm-6 col-md-4">
        <div className="form-group">
          <label>Nacionalidade <span>*</span></label>
          <Select
            className={`react-select ${!data.nationality ? 'is-invalid' : ''}`}
            placeholder=""
            id="nationalityValue"
            name="nationality"
            components={animatedComponents}
            options={countries.map((country: any) => ({ value: country.label, label: country.label }))}
            value={data.nationality ? { value: data.nationality, label: data.nationality } : null}
            isSearchable={true}
            isDisabled={false}
            isLoading={false}
            isClearable={true}
            isRtl={false}
            onChange={(e: any) => setData({ ...data, nationality: e.value })}
            closeMenuOnSelect={true}
            menuPosition="fixed"
            required
          />
        </div>
      </div>

      <div className="col-12 col-sm-4 col-md-6">
        <div className="form-group">
          <label>Whatsapp <span>*</span></label>
          <PhoneInput
            defaultCountry="br"
            countries={countriesWithBrNewMask}
            inputClassName="w-100"
            name="whatsapp"
            value={data.whatsapp ?? ''}
            onChange={(whatsapp) => setData({ ...data, whatsapp })}
            required
          />
        </div>
      </div>
      <div className="col-12 col-sm-4 col-md-6">
        <div className="form-group">
          <label>E-mail <span>*</span></label>
          <input
            type="text"
            placeholder=""
            className={`form-control ${!data.email ? 'is-invalid' : ''}`}
            name="email"
            value={data.email}
            onChange={(e) => setData({ ...data, email: e.target.value })}
            required
          />
        </div>
      </div>

      <div className="col-12">
        <div className="d-flex align-items-center mb-2">
          <div className="custom-control custom-switch" style={{ top: '1px' }}>
            <input
              className="custom-control-input d-flex align-items-center"
              type="checkbox"
              role="switch"
              id="has_clinic"
              onChange={handleHasClinic}
              checked={hasClinic}
            />
            <label className="custom-control-label" htmlFor="has_clinic"></label>
          </div>
          <h6 className="mb-0">Você possui clínica para tratamento?</h6>
        </div>
        {hasClinic && (
          <ClinicCascadeSelect
            value={clinicData}
            onChange={setClinicData}
          />
        )}
        <input type="hidden" name="has_clinic" value={hasClinic ? '1' : '0'} />
        {hasClinic && clinicData.clinic_id !== '' && (
          <input type="hidden" name="clinic_id" value={clinicData.clinic_id} />
        )}
      </div>
    </div>
  )
}

export default ProfilePatient
